import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import {
  Home,
  Categories,
  Welcome,
  Login,
  Register,
  Account,
  Notifications,
  Success,
  ForgotPassword,
  ProductDetail,
  Favorites,
  Category,
  Search,
  Brand,
  AccountDetail,
  Addresses,
  AddressUpsert,
  Order,
  Orders,
  PrivacyPolicy,
  Cart,
} from "./pages";

import BottomBar from "./components/BottomBar";
import { Splash, LicenceExpired } from "./private_pages";
import axios from "axios";
import config from "./config";
import ikas from "./providers/ikas";

const date = new Date().getTime() / 1000;

const App = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    cart: {},
    customer: {},
    orders: [],
    favorites: [],
    system: {
      safeArea: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      pixelRatio: 1,
      fcmToken: "",
    },
  });

  const navigate = useNavigate();

  const dataSetter = ({ key, value, remove }) => {
    if (key) {
      if (remove) {
        setData((prev) => {
          const newData = { ...prev };
          delete newData[key];
          return newData;
        });
      } else {
        setData((prev) => ({
          ...prev,
          [key]: value,
        }));
      }
    }
  };
  const sendMessageToReactNative = (message) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  };

  const checkCart = async () => {
    try {
      const cartId = localStorage.getItem("cartId");
      if (cartId) {
        const cart = await ikas.getCart();
        if (cart && cart.value) {
          dataSetter(cart);
        }
      }
    } catch (error) {
      localStorage.removeItem("cartId");
      console.error("Sepet kontrolü sırasında hata:", error);
    }
  };
  const checkLogin = async () => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      if (token) {
        await ikas.customerRefreshToken(token);

        const customer = await ikas.getMyCustomer();
        console.log(customer);
        if (!customer.value) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
        } else {
          dataSetter(customer);
        }
      }
    } catch (error) {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      console.error("Login kontrolü sırasında hata:", error);
    }
  };
  const checkFavorites = () => {
    try {
      const favorites =
        JSON.parse(localStorage.getItem("favorites")) ||
        JSON.parse(sessionStorage.getItem("favorites")) ||
        [];
      dataSetter({
        key: "favorites",
        value: favorites,
      });
    } catch (error) {
      console.error("Favoriler kontrolü sırasında hata:", error);
    }
  };
  const initializeData = async () => {
    setLoading(true);
    sendMessageToReactNative({ type: "init", value: "mobligo-ikas" });
    await checkLogin();
    await checkCart();
    checkFavorites();
    setLoading(false);
    sendMessageToReactNative({ type: "loaded", value: "mobligo-ikas" });
  };
  const handleReactNativeMessage = async (event) => {
    if (
      !event.data ||
      typeof event.data !== "string" ||
      !event.data.trim().startsWith("{")
    )
      return;

    try {
      const message = JSON.parse(event?.data);

      switch (message.type) {
        case "init":
          dataSetter({
            key: "system",
            value: message.payload,
          });
          break;
        case "checkout":
          if (message.payload === "success") {
            localStorage.removeItem("cartId");
            await checkCart();
            await checkLogin();
            navigate("success");
          }
          if (message.payload === "failure") {
            navigate("failure");
          }
          break;
        case "notification-received":
          //TODO:handle notification received
          break;
        case "notification-pressed":
          const { url, params } = message.payload;
          const parsedParams = JSON.parse(params);
          navigate(url, { state: parsedParams });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Mesaj işleme hatası:", error);
    }
  };
  const api = async ({ method, url, data }) => {
    const request = await axios({
      method,
      url,
      headers: {
        "x-mobligo-app-header": process.env.REACT_APP_MOBLIGO_APP_API_KEY,
        "x-hostname": config.MOBLIGO_HOST_NAME,
        "x-mobligo-app-id": config.STORE_ID,
        "x-mobligo-customer-id": "", //şimdilik boş müşterinin bizdeki idsi
        "x-mobligo-provider-customer-id": data?.customer?.id ?? "", //müşterinini providerdaki id'si
        "x-mobligo-provider-id": config.MOBLIGO_PROVIDER_ID,
      },
      data,
    });
    return request;
  };

  useEffect(() => {
    initializeData();
    document.addEventListener("message", handleReactNativeMessage);
    window.addEventListener("message", handleReactNativeMessage);
    return () => {
      window.removeEventListener("message", handleReactNativeMessage);
      document.removeEventListener("message", handleReactNativeMessage);
    };
  }, []);

  useEffect(() => {
    const subscribeTopic = (fcmToken, topic) => {
      api({
        method: "POST",
        url: `${config.URL}/notification/topic/${topic}/${config.STORE_ID}/subscribe`,
        data: {
          fcmToken,
        },
      }).catch((error) => {
        console.error("FCM Token aboneliği sırasında hata:", error);
      });
    };
    if (data?.system?.fcmToken) {
      console.log("FCM Token:", data.system.fcmToken);
      subscribeTopic(data.system.fcmToken, "all");
    }
  }, [data?.system?.fcmToken]);

  if (date > config.LICENCE_EXPIRE_TIME) {
    return <LicenceExpired />;
  }

  if (loading) {
    return <Splash />;
  }
  return (
    <div>
      <Routes>
        <Route
          path="/"
          element={<Home data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/categories"
          element={<Categories data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/welcome"
          element={<Welcome data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/login"
          element={<Login data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/register"
          element={<Register data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/account"
          element={<Account data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/notifications"
          element={<Notifications data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/success"
          element={<Success data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/forgot-password"
          element={<ForgotPassword data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/product"
          element={<ProductDetail data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/favorites"
          element={<Favorites data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/category"
          element={<Category data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/search"
          element={<Search data={data} dataSetter={dataSetter} />}
        />

        <Route
          path="/brand"
          element={<Brand data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/account/detail"
          element={<AccountDetail data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/account/addresses"
          element={<Addresses data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/account/address/upsert"
          element={<AddressUpsert data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/account/orders"
          element={<Orders data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/account/order"
          element={<Order data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/privacy-policy"
          element={<PrivacyPolicy data={data} dataSetter={dataSetter} />}
        />
        <Route
          path="/cart"
          element={<Cart data={data} dataSetter={dataSetter} />}
        />
      </Routes>
      <BottomBar data={data} />
    </div>
  );
};
const AppWrapper = () => {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
};
export default AppWrapper;
